import type { BasisNode } from '@wolhasudang/engine';

export interface ResultGuidance {
  title: string;
  body: string;
}

const missingInputGuidance: Record<string, ResultGuidance> = {
  ordinaryHourlyRateConfirmed: {
    title: '통상시급을 확인해주세요',
    body: '급여명세나 근로계약서에서 확인한 통상시급을 입력하고 확인 표시를 해주세요.',
  },
  establishmentSizeConfirmed: {
    title: '사업장 규모를 확인해주세요',
    body: '상시 근로자 5인 이상 여부를 확인한 뒤 근무 조건에 표시해주세요.',
  },
  supportedWorkTimeScheme: {
    title: '근로시간제도를 확인해주세요',
    body: '현재 자동 계산은 표준 근로시간제만 지원해요. 다른 제도라면 결과를 확정하지 않아요.',
  },
  supportedPublicServantRateTable: {
    title: '직군과 계급을 다시 확인해주세요',
    body: '선택한 직군·계급에 맞는 공식 단가를 찾을 수 없어 결과를 확정하지 않아요.',
  },
  institutionRuleConfirmed: {
    title: '기관이 인정한 시간을 확인해주세요',
    body: '시간외·야간·휴일 근무로 인정된 시간과 일수를 확인한 뒤 표시해주세요.',
  },
  weeklyScheduledHoursConfirmed: {
    title: '주 소정근로시간을 확인해주세요',
    body: '근로계약서나 사업장 안내에서 주 소정근로일과 시간을 확인해야 주휴 참고값을 계산할 수 있어요.',
  },
  MISSING_WAGE_TERM: {
    title: '해당 날짜의 시급 기준을 확인해주세요',
    body: '요율 이력이 시작되기 전 날짜는 임의로 외삽하지 않고 결과를 보류해요.',
  },
};

const warningGuidance: Record<string, ResultGuidance> = {
  ACTUAL_BREAK_UNCONFIRMED: {
    title: '실제 휴게시간을 확인해주세요',
    body: '휴게를 아직 모르는 날은 계산 합계에서 제외했어요. 기록을 수정하기 전에는 월 합계·차액을 확정하지 않아요.',
  },
  UNSUPPORTED_WORK_TIME_SCHEME: {
    title: '선택한 근로시간제도는 자동 계산 범위 밖이에요',
    body: '결과를 확정하지 않고 참고용으로만 보여드려요.',
  },
  LEGAL_REVIEW_REQUIRED_OVERTIME_HOLIDAY: {
    title: '연장·휴일 중복 기준을 더 확인해야 해요',
    body: '적용 기준이 확정되기 전에는 해당 금액과 차액을 확정하지 않아요.',
  },
  STATUTORY_PREMIUM_NOT_APPLIED_UNDER5: {
    title: '5인 미만 사업장 기준으로 계산했어요',
    body: '현재 입력한 사업장 규모에 따라 법정 가산을 적용하지 않았어요.',
  },
  UNSUPPORTED_RATE_TABLE: {
    title: '선택한 공무원 단가를 확인해주세요',
    body: '공식 기준표에서 단가를 찾을 수 없어 결과를 확정하지 않아요.',
  },
  WEEKLY_HOLIDAY_RANGE: {
    title: '주휴 참고값이 범위로 표시돼요',
    body: '일부 주의 출근·근로관계 정보가 확인되지 않아 단일값으로 확정하지 않아요.',
  },
  BELOW_MINIMUM_WAGE: {
    title: '최저임금 기준보다 낮은 시급이에요',
    body: '2026년 최저임금은 시간당 10,320원이에요. 입력한 시급과 근로계약·급여명세를 함께 확인해보세요.',
  },
};

const formulaLabels: Record<string, string> = {
  'minutes × ordinaryHourlyRate ÷ 60 × premiumRate': '대상 시간 × 통상시급 × 가산율',
  '주 소정근로시간 ÷ max(소정근로일, 5) × 통상시급': '주 소정근로시간 ÷ max(소정근로일, 5) × 통상시급',
  userConfiguredMonthlyAmount: '입력한 월 정액',
  'unitAmount × matchingShiftCount': '입력한 단가 × 적용 근무 횟수',
  'recognized minutes × official overtime hourly ÷ 60': '기관 인정 시간외 근무시간 × 공식 시간외 단가',
  'recognized minutes × official night hourly ÷ 60': '기관 인정 야간 근무시간 × 공식 야간 단가',
  'recognized days × official holiday daily': '기관 인정 휴일 근무일수 × 공식 휴일 단가',
};

const sourceLabels: Record<string, string> = {
  user: '내가 확인한 값',
  table: '공식 기준표',
  derived: '근무 기록으로 계산',
};

export function resultGuidance(missingInputs: string[], warnings: string[]): ResultGuidance[] {
  const values = [
    ...missingInputs.map((code) => missingInputGuidance[code] ?? {
      title: '근무 조건을 더 확인해주세요',
      body: '계산에 필요한 정보가 부족해 결과를 확정하지 않아요.',
    }),
    ...warnings.map((code) => warningGuidance[code] ?? {
      title: '적용 조건을 더 확인해주세요',
      body: '현재 입력만으로 확정하기 어려운 조건이 있어요.',
    }),
  ];
  return values.filter((value, index) => values.findIndex((candidate) => candidate.title === value.title && candidate.body === value.body) === index);
}

export function basisFormulaLabel(formula: string): string {
  return formulaLabels[formula] ?? '입력한 근무와 적용 기준으로 계산';
}

export function basisVariableLabel(variable: BasisNode['variables'][number], formula: string): { label: string; value: string; source: string } {
  const label = ({
    minutes: '대상 시간',
    ordinaryHourlyRate: '확인한 통상시급',
    premiumRate: '적용 가산율',
    unitAmount: '입력한 단가',
    matchingShiftCount: '적용 근무 횟수',
    weeklyScheduledMinutes: '주 소정근로시간',
    weeklyHolidayWeekCount: '주휴 산출 주 수',
    quantity: formula.includes('days') ? '기관 인정 휴일' : '기관 인정 시간',
    officialRate: formula.includes('daily') ? '공식 휴일 단가' : '공식 시간당 단가',
  } as Record<string, string>)[variable.name] ?? '확인값';

  let value = variable.value;
  if (variable.name === 'minutes') value = formatMinutes(variable.value);
  if (variable.name === 'ordinaryHourlyRate' || variable.name === 'unitAmount') value = formatMoney(variable.value);
  if (variable.name === 'premiumRate') value = formatRatio(variable.value);
  if (variable.name === 'matchingShiftCount') value = `${variable.value}회`;
  if (variable.name === 'quantity') value = `${variable.value}${formula.includes('days') ? '일' : '분'}`;
  if (variable.name === 'officialRate') value = `${formatNumber(variable.value)}원/${formula.includes('daily') ? '일' : '시간'}`;

  return { label, value, source: sourceLabels[variable.source] ?? '계산에 사용한 값' };
}

function formatMinutes(value: string): string {
  const [min, max] = value.split('-');
  if (!min || !max || min === max) return `${formatNumber(min ?? value)}분`;
  return `${formatNumber(min)}~${formatNumber(max)}분`;
}

function formatMoney(value: string): string {
  return `${formatNumber(value)}원`;
}

function formatNumber(value: string): string {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('ko-KR') : value;
}

function formatRatio(value: string): string {
  const [numerator, denominator] = value.split('/').map(Number);
  return numerator !== undefined && denominator ? `${Math.round((numerator / denominator) * 100)}%` : value;
}
